import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Box, createStyles} from '@mantine/core';
import React, {useCallback, useMemo, useRef} from 'react';
import {ReactFlowProvider} from 'reactflow';

import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  ConfiguratorResultType,
  useProcessFlowchartEditorConfigurator,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';
import {buildExecutionEditorConfigurator} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/executionFlowchartConfigurator';
import {
  ExecutionFlowchartRuntimeContext,
  type ExecutionFlowchartRuntimeValue,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartRuntimeContext';
import ProcessFlowchartConfigEditorContent from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorContent';
import type {FlowchartNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import type {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import type {FlowchartConfigMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/SingletonTaskSchemasContext';
import TransitionsManager from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsManager';
import type {
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/BaseFlowchartConfigStateManager';
import {ensureDefaultProcessesPluginRegistered} from 'processes/settings/ProcessTaskSchemaEditor';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';

import type {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {buildDraftForViewer, convertViewerFlowchartToStatic} from './buildDisplayOnlyDraftFromStructure';
import NoFlowchartViewer from './NoFlowchartViewer';
import type {ViewerStaticTemplateFlowchart} from './ReadOnlyFlowchartTypes';
import useReadOnlyFlowchartRootTaskSchema, {
  ReadOnlyFlowchartRootTaskSchemaResultType,
} from './useReadOnlyFlowchartRootTaskSchema';

export interface ReadOnlyFlowchartProps {
  flowchart: ViewerStaticTemplateFlowchart;
  rootTaskSchemaId: string;
  onNodeNavigateClick?: (nodeConfigId: string) => void;
  selectedNodeConfigId?: string;
}

const useStyles = createStyles(() => ({
  root: {
    height: '100%',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

// --- Noop / stub values for ProcessFlowchartConfigEditorContent ---

const NOOP_LIVE_STATE_CHANGES: LiveStateChanges = {
  dirtyState: [false, noop],
  readonlyState: [false, noop],
  isSaveInFlightState: [false, noop],
  isAutosaverPausedState: [false, noop],
  isAutosaverBlockedState: [false, noop],
};

const EMPTY_SINGLETON_SCHEMAS: Partial<
  Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>
> = {};

// --- Component ---

function ReadOnlyFlowchartContent(props: ReadOnlyFlowchartProps): JSX.Element {
  const configuratorResult = useProcessFlowchartEditorConfigurator(
    DefaultProcessesPlugin.FLOWCHART_TYPE_NAME
  );

  const executionEditorConfigurator = useMemo(() => {
    if (configuratorResult.resultType !== ConfiguratorResultType.SUCCESS) {
      return null;
    }
    return buildExecutionEditorConfigurator(
      configuratorResult.configurator,
      FlowchartEditorMode.EXECUTION_VIEWER
    );
  }, [configuratorResult]);

  const rootTaskSchemaResult = useReadOnlyFlowchartRootTaskSchema(props.rootTaskSchemaId);

  const staticFlowchart = useMemo(() => convertViewerFlowchartToStatic(props.flowchart), [props.flowchart]);
  const flowchartConfigId = `execution-viewer-${props.rootTaskSchemaId}`;

  const draftFlowchartConfigAndMetadata = useMemo(
    () =>
      executionEditorConfigurator != null
        ? buildDraftForViewer(staticFlowchart, executionEditorConfigurator.flowchartTypeName)
        : null,
    [staticFlowchart, executionEditorConfigurator]
  );

  const setDraftFlowchartConfigAndMetadata = useCallback(
    (
      _updater: (current: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
      _saveImmediately?: boolean,
      _skipSettingDirtyState?: boolean
    ): Promise<FlowchartConfigMetadata | null> => Promise.resolve(null),
    []
  );

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const runtimeContextValue: Pick<
    ExecutionFlowchartRuntimeValue,
    'onNodeNavigateClick' | 'selectedNodeConfigId'
  > | null = useMemo(() => {
    if (props.onNodeNavigateClick == null && props.selectedNodeConfigId == null) {
      return null;
    }
    return {
      onNodeNavigateClick: props.onNodeNavigateClick,
      selectedNodeConfigId: props.selectedNodeConfigId,
    };
  }, [props.onNodeNavigateClick, props.selectedNodeConfigId]);

  if (configuratorResult.resultType === ConfiguratorResultType.ERROR) {
    return <NoFlowchartViewer message="Unable to load flowchart configuration" />;
  }

  if (rootTaskSchemaResult.type === ReadOnlyFlowchartRootTaskSchemaResultType.ERROR) {
    return <NoFlowchartViewer message="Unable to load the flowchart root schema" />;
  }

  if (rootTaskSchemaResult.type === ReadOnlyFlowchartRootTaskSchemaResultType.INACCESSIBLE) {
    return <NoFlowchartViewer message="The flowchart is not accessible" />;
  }

  if (
    executionEditorConfigurator == null ||
    rootTaskSchemaResult.type === ReadOnlyFlowchartRootTaskSchemaResultType.PENDING ||
    draftFlowchartConfigAndMetadata == null
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const rootTaskSchema = rootTaskSchemaResult.schema;

  return (
    <ReactFlowProvider>
      <SingletonTaskSchemasContext.Provider value={EMPTY_SINGLETON_SCHEMAS}>
        <ReactFlowLockedContext.Provider value={false}>
          <ExecutionFlowchartRuntimeContext.Provider
            value={runtimeContextValue as ExecutionFlowchartRuntimeValue | null}
          >
            <FlowchartSidepanelManager
              getNodeNameById={getNodeNameById}
              flowchartType={executionEditorConfigurator.flowchartTypeName}
            >
              <TransitionsManager draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}>
                <ProcessFlowchartConfigEditorContent
                  getNodeByIdRef={getNodeByIdRef}
                  flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
                  flowchartEditorConfigurator={executionEditorConfigurator}
                  editorMode={FlowchartEditorMode.EXECUTION_VIEWER}
                  allSchemasById={{}}
                  rootTaskSchema={rootTaskSchema}
                  flowchartTaskDefinitionId=""
                  flowchartConfigId={flowchartConfigId}
                  taskDefinitionsResult={null}
                  didInitialConfigHaveConditionalMapping={false}
                  didInitialFlowchartHaveChainedRouters={false}
                  liveStateChanges={NOOP_LIVE_STATE_CHANGES}
                  locked={false}
                  validationErrors={[]}
                  draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
                  setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
                />
              </TransitionsManager>
            </FlowchartSidepanelManager>
          </ExecutionFlowchartRuntimeContext.Provider>
        </ReactFlowLockedContext.Provider>
      </SingletonTaskSchemasContext.Provider>
    </ReactFlowProvider>
  );
}

/**
 * Read-only flowchart viewer for execution/lineage contexts. Converts pre-built
 * viewer structure data into the static flowchart format and renders through
 * {@link ProcessFlowchartConfigEditorContent} in EXECUTION_VIEWER mode.
 */
export default function ReadOnlyFlowchart(props: ReadOnlyFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  ensureDefaultProcessesPluginRegistered({ignoreFlowchartConfigFlag: true});

  return (
    <Box className={classes.root} {...getTestProps({component: 'ReadOnlyFlowchart', element: 'root'})}>
      <ReactFlowWrapper fullWidth={true} deferUntilMeasured={true}>
        <ReadOnlyFlowchartContent {...props} />
      </ReactFlowWrapper>
    </Box>
  );
}
